/* eslint-disable no-param-reassign */
import { Applicant, defaultApplicant, PropertyIdentificationConfig } from '@common/entities';
import { createSlice } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

import { sortApplicants } from 'utils/leadProfileUtils';

export interface ApplicationForm {
  id: string;
  name: string;
  description: string;
  type: string;
  clientId: number;
  pages: any[];
  versionId: string;
  applicationId: string;
  occupancyType?: string;
  identificationConfig?: PropertyIdentificationConfig;
}

export interface LeadProfileApplicant {
  responseId: string;
  entrataApplicationId?: number;
  entrataApplicantId?: number;
  leaseId?: number;
  firstName: string;
  lastName: string;
  email: string;
  responseDetails: any;
  relationshipType: string;
  startDate?: string;
  updatedDate?: string;
  paymentProcessed?: boolean;
  paymentAccountId?: string;
  entrataCustomerRelationshipId?: number;
  marketingName?: string;
}

export interface ApplicantsState {
  primaryResponseId: string;
  primaryApplicant: Applicant;
  applicationForm: ApplicationForm;
  applicants: LeadProfileApplicant[];
  applicantResponses: Record<string, any>;
}

const initialState: ApplicantsState = {
  primaryResponseId: '',
  applicationForm: {
    id: '',
    name: '',
    description: '',
    type: '',
    clientId: 0,
    pages: [],
    versionId: '',
    applicationId: '',
    occupancyType: '',
  },
  primaryApplicant: defaultApplicant,
  applicants: [],
  applicantResponses: {},
};

export const leadProfileSlice = createSlice({
  name: 'leadProfile',
  initialState,
  reducers: {
    setApplicantsData: (state, action) => {
      const next = { ...state, ...action.payload };
      // Keep applicants in canonical tab order so currentTabIndex maps to the same
      // applicant in every consumer (nav, save, selectors).
      if (Array.isArray(next.applicants)) {
        next.applicants = sortApplicants(next.applicants);
      }
      return next;
    },
    updateApplicantResponses: (state, action) => {
      const { responseId, data, relationshipType, entrataCustomerRelationshipId } = action.payload;

      if (!state.applicantResponses[responseId]) {
        state.applicantResponses[responseId] = {};
      }

      state.applicantResponses[responseId] = {
        ...state.applicantResponses[responseId],
        ...data,
      };

      const applicant = state.applicants.find((a) => a.responseId === responseId);

      if (applicant) {
        if (data?.applicant_name_first && typeof data.applicant_name_first === 'string') {
          applicant.firstName = data.applicant_name_first;
        }

        if (data?.applicant_name_last && typeof data.applicant_name_last === 'string') {
          applicant.lastName = data.applicant_name_last;
        }

        if (relationshipType) {
          applicant.relationshipType = relationshipType;
        }

        if (entrataCustomerRelationshipId) {
          applicant.entrataCustomerRelationshipId = entrataCustomerRelationshipId;
        }
      }
    },

    addApplicant: (state, action) => {
      state.applicants.push(action.payload);
      // Re-sort so the new applicant lands in its canonical tab position.
      state.applicants = sortApplicants(state.applicants);
    },
    removeApplicantByResponseId: (state, action: { payload: { responseId: string } }) => {
      const { responseId } = action.payload;
      if (!responseId) {
        return;
      }
      state.applicants = state.applicants.filter((a) => a.responseId !== responseId);
      delete state.applicantResponses[responseId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
  },
});

export const {
  setApplicantsData,
  updateApplicantResponses,
  addApplicant,
  removeApplicantByResponseId,
} = leadProfileSlice.actions;
export default leadProfileSlice.reducer;
